import { expValEquals } from '@confluence/feature-experiments';
import { cfetch } from '@confluence/network';

import { MarkdownImportError } from './MarkdownImportService.ts';

type CreateMarkdownFolderInput = {
	spaceId: string;
	title: string;
	signal: AbortSignal;
};

type FindRootMarkdownFolderInput = {
	spaceKey: string;
	title: string;
	signal: AbortSignal;
};

type V2FolderResponse = {
	id?: string;
};

type V1ContentSearchResponse = {
	totalSize?: number;
	results?: ReadonlyArray<{
		breadcrumbs?: ReadonlyArray<unknown>;
		content?: {
			id?: string;
			type?: string;
			title?: string;
		};
	}>;
};

const assertFolderResolutionEnabled = () => {
	if (!expValEquals('twcg_2627_markdown_file_import_into_confluence', 'isEnabled', true)) {
		throw new MarkdownImportError('experiment_disabled');
	}
};

const assertNotAborted = (signal: AbortSignal) => {
	if (signal.aborted) {
		throw new MarkdownImportError('aborted');
	}
};

const escapeCqlString = (value: string) => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

export const findRootMarkdownFolder = async ({
	spaceKey,
	title,
	signal,
}: FindRootMarkdownFolderInput): Promise<string | undefined> => {
	try {
		assertNotAborted(signal);
		assertFolderResolutionEnabled();

		const cql = `space = "${escapeCqlString(spaceKey)}" AND title ~ "${escapeCqlString(title)}"`;
		const searchParams = new URLSearchParams({
			cql,
			limit: '50',
		});
		const response = await cfetch(`/wiki/rest/api/search?${searchParams.toString()}`, {
			method: 'GET',
			credentials: 'include',
			headers: { Accept: 'application/json' },
			signal,
		});
		const searchResult = (await response.json()) as V1ContentSearchResponse;
		assertNotAborted(signal);

		const results = searchResult.results ?? [];
		const matchingFolders = results.filter(({ breadcrumbs, content }) => {
			return (
				content?.type === 'folder' &&
				content.title === title &&
				Array.isArray(breadcrumbs) &&
				breadcrumbs.length === 0 &&
				typeof content.id === 'string'
			);
		});
		const existingFolderId = matchingFolders[0]?.content?.id;
		const isTruncated =
			typeof searchResult.totalSize === 'number' && searchResult.totalSize > results.length;

		if (!existingFolderId && isTruncated) {
			throw new Error(
				'Folder search results were truncated before an exact root folder match could be confirmed',
			);
		}

		return existingFolderId;
	} catch (error) {
		if (signal.aborted) {
			throw new MarkdownImportError('aborted');
		}

		throw error;
	}
};

export const createMarkdownFolder = async ({
	spaceId,
	title,
	signal,
}: CreateMarkdownFolderInput): Promise<string> => {
	try {
		assertNotAborted(signal);

		if (!/^\d+$/.test(spaceId)) {
			throw new MarkdownImportError('invalid_destination');
		}

		assertFolderResolutionEnabled();

		const response = await cfetch('/wiki/api/v2/folders', {
			method: 'POST',
			credentials: 'include',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ spaceId, title }),
			signal,
		});
		const folder = (await response.json()) as V2FolderResponse;
		assertNotAborted(signal);

		if (!folder.id) {
			throw new Error('The create folder response did not contain an ID');
		}

		return folder.id;
	} catch (error) {
		if (signal.aborted) {
			throw new MarkdownImportError('aborted');
		}

		throw error;
	}
};
